import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Plus, Trash2 } from "lucide-react";
import GridPage from "components/layout/GridPage";
import Loading from "components/common/Loading";
import TextField from "components/common/TextField";
import Select from "components/common/Select";
import Checkbox from "components/common/Checkbox";
import useIsMobile from "components/hooks/useIsMobile";
import { ICON_OPTIONS } from "components/forum/ResourceSection";
import { RESOURCE_COLUMNS } from "components/forum/ResourcePageGrid";
import {
  useDeleteResourceSection,
  useGetResourcePage,
  useListResourceSections,
  useSetResourcePlacements,
  useUpsertResourceSection,
} from "services/resources";
import { ResourceSection } from "protogen/resources_service_pb";

const EMPTY = "";

type LinkDraft = {
  name: string;
  href: string;
  icon: string;
  targetNew: boolean;
};

const SectionEditorDialog = ({
  section,
  onClose,
  onSaved,
}: {
  section: ResourceSection | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [title, setTitle] = useState(section?.title || "");
  const [links, setLinks] = useState<LinkDraft[]>(
    (section?.links || []).map((l) => ({
      name: l.name,
      href: l.href,
      icon: l.icon || "",
      targetNew: l.targetNew,
    })),
  );
  const { request, loading } = useUpsertResourceSection();

  const updateLink = (index: number, patch: Partial<LinkDraft>) => {
    setLinks((prev) =>
      prev.map((l, i) => (i === index ? { ...l, ...patch } : l)),
    );
  };

  const save = async () => {
    const resp = await request({
      ref: section?.ref || EMPTY,
      title,
      links: links.filter((l) => l.name && l.href),
    });
    if (resp) onSaved();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{section ? "Edit section" : "New section"}</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          pt: "8px",
        }}
      >
        <TextField
          label="Section title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
        />
        <Typography variant="h4">Links</Typography>
        {links.map((link, index) => (
          <Box
            key={index}
            sx={{ display: "flex", gap: "8px", alignItems: "center" }}
          >
            <TextField
              label="Name"
              value={link.name}
              onChange={(e) => updateLink(index, { name: e.target.value })}
              sx={{ flex: 1 }}
            />
            <TextField
              label="URL"
              value={link.href}
              onChange={(e) => updateLink(index, { href: e.target.value })}
              sx={{ flex: 2 }}
            />
            <Select
              value={link.icon}
              onChange={(e) => updateLink(index, { icon: e.target.value })}
              displayEmpty
              options={ICON_OPTIONS.map((opt) => ({
                label: opt || "no icon",
                value: opt,
              }))}
              sx={{ minWidth: "110px" }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={link.targetNew}
                  onChange={(e) =>
                    updateLink(index, { targetNew: e.target.checked })
                  }
                />
              }
              label="New tab"
            />
            <IconButton
              onClick={() =>
                setLinks((prev) => prev.filter((_, i) => i !== index))
              }
            >
              <Trash2 size={18} />
            </IconButton>
          </Box>
        ))}
        <Button
          startIcon={<Plus size={16} />}
          onClick={() =>
            setLinks((prev) => [
              ...prev,
              { name: "", href: "", icon: "", targetNew: false },
            ])
          }
          sx={{ alignSelf: "flex-start" }}
        >
          Add link
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={loading || !title}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SectionsTab = () => {
  const { data, loading, request } = useListResourceSections();
  const { request: deleteSection } = useDeleteResourceSection();
  const [editing, setEditing] = useState<ResourceSection | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    request();
  }, []);

  const refetch = () => {
    setEditing(null);
    setCreating(false);
    request();
  };

  const onDelete = async (section: ResourceSection) => {
    if (
      !window.confirm(
        `Delete "${section.title}"? It will be removed from all pages.`,
      )
    )
      return;
    await deleteSection({ ref: section.ref });
    request();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <Button
        variant="text"
        startIcon={<Plus size={16} />}
        onClick={() => setCreating(true)}
        sx={{ alignSelf: "flex-start" }}
      >
        New section
      </Button>
      {loading && <Loading />}
      {(data?.sections || []).map((section) => (
        <Box
          key={section.ref}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "12px 16px",
          }}
        >
          <Box>
            <Typography variant="h4">{section.title}</Typography>
            <Typography variant="bodySmall" color="text.secondary">
              {section.links.length} link{section.links.length === 1 ? "" : "s"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Button variant="text" onClick={() => setEditing(section)}>
              Edit
            </Button>
            <Button
              variant="text"
              color="error"
              onClick={() => onDelete(section)}
              disableRipple
              sx={{ "&:hover": { backgroundColor: "transparent" } }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      ))}
      {(creating || editing) && (
        <SectionEditorDialog
          section={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={refetch}
        />
      )}
    </Box>
  );
};

const PlacementTab = ({ page }: { page: "resources" | "help" }) => {
  const { data: sectionsData, request: listSections } =
    useListResourceSections();
  const { data: pageData, loading, request: getPage } = useGetResourcePage();
  const { request: savePlacements, loading: saving } =
    useSetResourcePlacements();
  // grid[row][column] = sectionRef | ""
  const [grid, setGrid] = useState<string[][]>([]);

  useEffect(() => {
    listSections();
    getPage({ page });
  }, [page]);

  useEffect(() => {
    if (!pageData) return;
    let maxRow = -1;
    pageData.placements.forEach((p) => (maxRow = Math.max(maxRow, p.row)));
    const next: string[][] = Array.from({ length: maxRow + 1 }, () =>
      RESOURCE_COLUMNS.map(() => EMPTY),
    );
    pageData.placements.forEach((p) => {
      if (p.section) next[p.row][p.column] = p.section.ref;
    });
    setGrid(next);
  }, [pageData]);

  const sections = sectionsData?.sections || [];
  const setCell = (row: number, column: number, ref: string) => {
    setGrid((prev) =>
      prev.map((r, i) =>
        i === row ? r.map((c, j) => (j === column ? ref : c)) : r,
      ),
    );
  };

  const save = async () => {
    const placements: { column: number; row: number; sectionRef: string }[] =
      [];
    grid.forEach((cols, row) =>
      cols.forEach((ref, column) => {
        if (ref) placements.push({ column, row, sectionRef: ref });
      }),
    );
    await savePlacements({ page, placements });
    getPage({ page });
  };

  if (loading && !pageData) return <Loading />;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <Typography variant="bodySmall" color="text.secondary">
        {page === "help"
          ? "Each row is a grid row on the page. Leave a box empty to create an intentional gap."
          : "Sections flow down each column independently. Empty boxes are skipped, not shown as gaps."}
      </Typography>
      {grid.map((cols, row) => (
        <Box key={row} sx={{ display: "flex", gap: "16px" }}>
          {cols.map((ref, column) => (
            <Select
              key={column}
              value={ref}
              displayEmpty
              onChange={(e) => setCell(row, column, e.target.value)}
              options={[
                { label: "— empty —", value: EMPTY },
                ...sections.map((section) => ({
                  label: section.title,
                  value: section.ref,
                })),
              ]}
              sx={{ flex: 1 }}
            />
          ))}
        </Box>
      ))}
      <Box sx={{ display: "flex", gap: "8px" }}>
        <Button
          variant="text"
          startIcon={<Plus size={16} />}
          onClick={() =>
            setGrid((prev) => [...prev, RESOURCE_COLUMNS.map(() => EMPTY)])
          }
        >
          Add row
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          Save layout
        </Button>
      </Box>
    </Box>
  );
};

export default () => {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState(0);

  return (
    <GridPage
      sx={{
        margin: isMobile ? "" : "64px auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px" : "0",
      }}
    >
      <Typography variant="title">Resources & Help CMS</Typography>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ marginBottom: "24px" }}
      >
        <Tab label="Sections" />
        <Tab label="Resources page" />
        <Tab label="Help page" />
      </Tabs>
      {tab === 0 && <SectionsTab />}
      {tab === 1 && <PlacementTab page="resources" />}
      {tab === 2 && <PlacementTab page="help" />}
    </GridPage>
  );
};
